import React, { useState, useEffect } from 'react';
import { ContentBuilder, generateHTML, parseHTMLToElements } from '../../../content-builder';
import { FormTextArea } from '../../../FormTextArea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../Tabs';
import { getNavigationIcon } from '../../../navigation/NavigationIcons';
import Button from '../../../Button';

const TEMPLATE_PRESETS = {
  blank: {
    elements: [],
    html: ''
  },
  waiver: {
    html: '<h2>Liability Waiver</h2><p>By signing this document, you acknowledge and agree to release and hold harmless the club from any liability arising from participation in club activities.</p><p>I understand that participation in sports activities involves inherent risks and I voluntarily assume all such risks.</p>',
    elements: null // Will be parsed from HTML
  },
  medical: {
    html: '<h2>Medical Information</h2><p>Please provide the following medical information for emergency purposes.</p><h3>Medical History</h3><p>List any known medical conditions, allergies, or medications:</p>',
    elements: null
  },
  concussion: {
    html: '<h2>Concussion Protocol</h2><p>I understand and acknowledge the risks of concussion and head injuries in sports activities.</p><p>I agree to report any symptoms of head injury immediately and follow all return-to-play protocols.</p>',
    elements: null
  }
};

/**
 * Step 2: Content Designer
 *
 * Purpose: Design template content using ContentBuilder or raw HTML
 *
 * Captures:
 * - Document content (HTML)
 * - ContentBuilder elements (JSON)
 * - Elements array for ContentBuilder
 *
 * Features:
 * - Visual ContentBuilder interface
 * - Raw HTML editing fallback
 * - Load from template presets
 */
const Step2_ContentDesigner = ({
  wizardData,
  onDataChange,
  onValidationChange
}) => {
  const [activeTab, setActiveTab] = useState('designer');
  const [errors, setErrors] = useState({});

  // Initialize elements from wizard data or selected template type
  useEffect(() => {
    // If we have a selected template type but no content yet, load the preset
    if (wizardData.selectedTemplateType && !wizardData.content && !wizardData.elements?.length) {
      loadTemplatePreset(wizardData.selectedTemplateType);
    }
  }, []); // Only run once on mount

  useEffect(() => {
    // Validate that content exists
    const newErrors = {};

    if (!wizardData.content?.trim() && (!wizardData.elements || wizardData.elements.length === 0)) {
      newErrors.content = 'Content is required';
    }

    const isValid = Object.keys(newErrors).length === 0;
    onValidationChange(isValid);
    setErrors(newErrors);
  }, [wizardData.content, wizardData.elements, onValidationChange]);

  const loadTemplatePreset = (templateType) => {
    const preset = TEMPLATE_PRESETS[templateType] || TEMPLATE_PRESETS.blank;

    if (preset.html) {
      // Parse HTML to elements
      const parsedElements = parseHTMLToElements(preset.html);

      onDataChange({
        ...wizardData,
        content: preset.html,
        elements: parsedElements,
        elements_json: JSON.stringify(parsedElements)
      });
    }
  };

  const handleElementsChange = (newElements) => {
    // Generate HTML for backward compatibility
    const html = generateHTML(newElements, { mode: 'form' });

    onDataChange({
      ...wizardData,
      content: html,
      elements: newElements,
      elements_json: JSON.stringify(newElements)
    });
  };

  const handleRawHtmlChange = (value) => {
    // Parse HTML to elements for ContentBuilder sync
    const parsedElements = value ? parseHTMLToElements(value) : [];

    onDataChange({
      ...wizardData,
      content: value,
      elements: parsedElements,
      elements_json: JSON.stringify(parsedElements)
    });
  };

  const handleLoadPreset = (templateType) => {
    loadTemplatePreset(templateType);
  };

  return (
    <div className="space-y-4">
      {/* Header with preset loader */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Design Template Content</h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleLoadPreset(wizardData.selectedTemplateType || 'blank')}
            disabled={!wizardData.selectedTemplateType || wizardData.selectedTemplateType === 'blank'}
          >
            Load Preset
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
        <p className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ opacity: 0.7 }}>{getNavigationIcon('target')}</span>
          Use the visual designer to build your document, or edit raw HTML directly. Both views stay in sync.
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="designer">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              {getNavigationIcon('edit')} Visual Designer
            </span>
          </TabsTrigger>
          <TabsTrigger value="html">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              {getNavigationIcon('settings')} Raw HTML
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="designer">
          <div style={{
            height: 'calc(100vh - 400px)',
            minHeight: '400px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <ContentBuilder
              mode="form"
              initialElements={wizardData.elements || []}
              onChange={handleElementsChange}
              showPreview={true}
            />
          </div>
        </TabsContent>

        <TabsContent value="html">
          <div className="space-y-4">
            <FormTextArea
              name="content"
              value={wizardData.content}
              onChange={(e) => handleRawHtmlChange(e.target.value)}
              placeholder="Enter HTML content here..."
              rows={15}
              error={errors.content}
              style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
            />
            <p className="text-sm text-gray-500">
              Edit raw HTML here. Changes will be reflected in the Visual Designer tab.
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Validation Error */}
      {errors.content && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p className="text-sm">{errors.content}</p>
        </div>
      )}
    </div>
  );
};

export { Step2_ContentDesigner };
